import { createReducer } from "@reduxjs/toolkit";
import {
    loadChemicals,
    createChemical,
    updateChemical,
    deleteChemical,
    loadReactions,
    loadReactionTypes,
    createReaction,
    updateReaction,
    deleteReaction,
    addChemicalToReaction,
    removeChemicalFromReaction,
    ILibraryChemical,
    ILibraryReaction,
    IReactionChemical,
    ILibraryMultiStepReaction,
    ILibraryReactionRouteStage,
    IReactionType,
    loadLibrarySummary,
    loadMultiStepReactions,
    loadReactionRouteSummaries,
    loadReactionRouteStages,
    loadReactionSummaries,
    createMultiStepReaction,
    updateMultiStepReaction,
    deleteMultiStepReaction,
} from "../actions/library";
import { resetLoadAttempts } from "../features/libraryUI";
import type {
    NotebookChemicalListResponse,
    NotebookLibrarySummaryResponse,
    NotebookReactionListResponse,
    NotebookReactionRouteListResponse,
    NotebookReactionRouteStageListResponse,
    NotebookReactionRouteSummary,
    NotebookReactionRouteSummaryListResponse,
    NotebookReactionSummary,
    NotebookReactionSummaryListResponse,
} from "../../data/models/schemas";
import {
    buildReactionLookup,
    buildRouteLookup,
    normalizeLibraryMultiStepReaction,
} from "../../utils/reactionRoute";
import { resolveRsaaErrorMessage } from "../utils/rsaaErrorMessage";

export interface ILibraryState {
    // Loading metadata
    chemicalsLoadingReason: "initial" | "search" | "page" | null;
    reactionsLoadingReason: "initial" | "search" | "page" | null;
    multiStepReactionsLoadingReason: "initial" | "search" | "page" | null;
    reactionRouteStagesLoadingReason: "initial" | "search" | "page" | null;
    reactionSummariesLoadingReason: "initial" | "search" | "page" | null;
    reactionRouteSummariesLoadingReason: "initial" | "search" | "page" | null;

    // Library Summary
    librarySummary: NotebookLibrarySummaryResponse | null;
    librarySummaryLoading: boolean;
    librarySummaryError: string | null;
    librarySummaryLoadAttempted: boolean;

    // Chemicals
    chemicals: ILibraryChemical[];
    chemicalsPagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };
    chemicalsLoading: boolean;
    chemicalsError: string | null;
    chemicalsLoadAttempted: boolean;

    // Reactions
    reactions: ILibraryReaction[];
    reactionsPagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };
    reactionsLoading: boolean;
    reactionsError: string | null;
    reactionsLoadAttempted: boolean;
    reactionSummaries: NotebookReactionSummary[];
    reactionSummariesPagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };
    reactionSummariesLoading: boolean;
    reactionSummariesError: string | null;
    reactionSummariesLoadAttempted: boolean;

    // Reaction Types
    reactionTypes: IReactionType[];
    reactionTypesLoading: boolean;
    reactionTypesError: string | null;

    // Multi-Step Reactions
    multiStepReactions: ILibraryMultiStepReaction[];
    multiStepReactionsPagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };
    multiStepReactionsLoading: boolean;
    multiStepReactionsError: string | null;
    multiStepReactionsLoadAttempted: boolean;
    reactionRouteSummaries: NotebookReactionRouteSummary[];
    reactionRouteSummariesPagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };
    reactionRouteSummariesLoading: boolean;
    reactionRouteSummariesError: string | null;
    reactionRouteSummariesLoadAttempted: boolean;

    // Reaction Route Stages
    reactionRouteStages: ILibraryReactionRouteStage[];
    reactionRouteStagesPagination: {
        page: number;
        pageSize: number;
        totalItems: number;
        totalPages: number;
    };
    reactionRouteStagesLoading: boolean;
    reactionRouteStagesError: string | null;
    reactionRouteStagesLoadAttempted: boolean;

    // UI State
    searchQuery: string;
    selectedTab:
        | "chemicals"
        | "reactions"
        | "multistep"
        | "stages"
        | "formulations"
        | "recent";
    isLibraryOpen: boolean;
}

const initialState: ILibraryState = {
    chemicalsLoadingReason: null,
    reactionsLoadingReason: null,
    multiStepReactionsLoadingReason: null,
    reactionRouteStagesLoadingReason: null,
    reactionSummariesLoadingReason: null,
    reactionRouteSummariesLoadingReason: null,

    librarySummary: null,
    librarySummaryLoading: false,
    librarySummaryError: null,
    librarySummaryLoadAttempted: false,

    chemicals: [],
    chemicalsPagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
    },
    chemicalsLoading: false,
    chemicalsError: null,
    chemicalsLoadAttempted: false,

    reactions: [],
    reactionsPagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
    },
    reactionsLoading: false,
    reactionsError: null,
    reactionsLoadAttempted: false,
    reactionSummaries: [],
    reactionSummariesPagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
    },
    reactionSummariesLoading: false,
    reactionSummariesError: null,
    reactionSummariesLoadAttempted: false,

    reactionTypes: [],
    reactionTypesLoading: false,
    reactionTypesError: null,

    multiStepReactions: [],
    multiStepReactionsPagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
    },
    multiStepReactionsLoading: false,
    multiStepReactionsError: null,
    multiStepReactionsLoadAttempted: false,
    reactionRouteSummaries: [],
    reactionRouteSummariesPagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
    },
    reactionRouteSummariesLoading: false,
    reactionRouteSummariesError: null,
    reactionRouteSummariesLoadAttempted: false,

    reactionRouteStages: [],
    reactionRouteStagesPagination: {
        page: 1,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
    },
    reactionRouteStagesLoading: false,
    reactionRouteStagesError: null,
    reactionRouteStagesLoadAttempted: false,

    searchQuery: "",
    selectedTab: "chemicals",
    isLibraryOpen: false,
};

const parseDeleteIdFromEndpoint = (
    endpoint: unknown,
    resource: "chemicals" | "reactions" | "reaction-routes"
): string | undefined => {
    if (typeof endpoint !== "string") return undefined;
    const match = endpoint.match(new RegExp(`/notebooks/${resource}/([^/?]+)`));
    if (!match?.[1]) return undefined;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
};

type LibraryListPayload<T> =
    | T[]
    | {
          items?: T[];
          page?: number;
          pageSize?: number;
          totalItems?: number;
          totalPages?: number;
      }
    | undefined
    | null;

const toArrayPayload = <T>(payload: LibraryListPayload<T>): T[] => {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (payload && Array.isArray(payload.items)) {
        return payload.items;
    }
    return [];
};

type LoadReasonMeta = {
    meta?: {
        loadReason?: ILibraryState["chemicalsLoadingReason"];
    };
};

type DeletedEntityMeta = {
    meta?: {
        deletedChemicalId?: string;
        deletedReactionId?: string;
        deletedRouteId?: string;
        rsaa_endpoint?: string;
    };
};

const listPagination = (
    payload:
        | NotebookChemicalListResponse
        | NotebookReactionListResponse
        | NotebookReactionRouteListResponse
        | NotebookReactionRouteStageListResponse
        | NotebookReactionSummaryListResponse
        | NotebookReactionRouteSummaryListResponse
        | undefined
): {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
} => ({
    page: payload?.page ?? 1,
    pageSize: payload?.pageSize ?? 0,
    totalItems: payload?.totalItems ?? 0,
    totalPages: payload?.totalPages ?? 0,
});

const library = createReducer(initialState, (builder) => {
    builder
        // Load Chemicals
        .addCase(loadChemicals.request.type, (state, action) => {
            const typedAction = action as typeof action & LoadReasonMeta;
            state.chemicalsLoading = true;
            state.chemicalsLoadingReason =
                typedAction.meta?.loadReason || "initial";
            state.chemicalsError = null;
            state.chemicalsLoadAttempted = true;
        })
        .addCase(loadChemicals.success, (state, action) => {
            state.chemicalsLoading = false;
            state.chemicalsLoadingReason = null;
            state.chemicals = toArrayPayload(action.payload);
            state.chemicalsPagination = listPagination(action.payload);
        })
        .addCase(loadChemicals.error, (state, action) => {
            state.chemicalsLoading = false;
            state.chemicalsLoadingReason = null;
            state.chemicalsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load chemicals"
            );
        })

        // Create Chemical
        .addCase(createChemical.request, (state) => {
            state.chemicalsLoading = true;
            state.chemicalsError = null;
        })
        .addCase(createChemical.success, (state, action) => {
            state.chemicalsLoading = false;
            state.chemicals.push(action.payload);
        })
        .addCase(createChemical.error, (state, action) => {
            state.chemicalsLoading = false;
            state.chemicalsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to create chemical"
            );
        })

        // Update Chemical
        .addCase(updateChemical.request, (state) => {
            state.chemicalsLoading = true;
            state.chemicalsError = null;
        })
        .addCase(updateChemical.success, (state, action) => {
            state.chemicalsLoading = false;
            const index = state.chemicals.findIndex(
                (c) => c.ui_id === action.payload.ui_id
            );
            if (index !== -1) {
                state.chemicals[index] = action.payload;
            }
        })
        .addCase(updateChemical.error, (state, action) => {
            state.chemicalsLoading = false;
            state.chemicalsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to update chemical"
            );
        })

        // Delete Chemical
        .addCase(deleteChemical.request, (state) => {
            state.chemicalsLoading = true;
            state.chemicalsError = null;
        })
        .addCase(deleteChemical.success.type, (state, action) => {
            const typedAction = action as typeof action & DeletedEntityMeta;
            state.chemicalsLoading = false;
            const deletedChemicalId =
                typedAction.meta?.deletedChemicalId ||
                parseDeleteIdFromEndpoint(
                    typedAction.meta?.rsaa_endpoint,
                    "chemicals"
                );
            if (deletedChemicalId) {
                state.chemicals = state.chemicals.filter(
                    (chemical) => chemical.ui_id !== deletedChemicalId
                );
            }
        })
        .addCase(deleteChemical.error, (state, action) => {
            state.chemicalsLoading = false;
            state.chemicalsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to delete chemical"
            );
        })

        // Load Library Summary
        .addCase(loadLibrarySummary.request, (state) => {
            state.librarySummaryLoading = true;
            state.librarySummaryError = null;
            state.librarySummaryLoadAttempted = true;
        })
        .addCase(loadLibrarySummary.success, (state, action) => {
            state.librarySummaryLoading = false;
            state.librarySummary = action.payload;
        })
        .addCase(loadLibrarySummary.error, (state, action) => {
            state.librarySummaryLoading = false;
            state.librarySummaryError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load library summary"
            );
        })

        // Load Reactions
        .addCase(loadReactions.request.type, (state, action) => {
            const typedAction = action as typeof action & LoadReasonMeta;
            state.reactionsLoading = true;
            state.reactionsLoadingReason =
                typedAction.meta?.loadReason || "initial";
            state.reactionsError = null;
            state.reactionsLoadAttempted = true;
        })
        .addCase(loadReactions.success, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsLoadingReason = null;
            state.reactions = toArrayPayload(action.payload);
            if (state.multiStepReactions.length > 0) {
                const reactionLookup = buildReactionLookup(state.reactions);
                const routeLookup = buildRouteLookup(state.multiStepReactions);
                state.multiStepReactions = state.multiStepReactions.map(
                    (route) =>
                        normalizeLibraryMultiStepReaction(
                            route,
                            reactionLookup,
                            routeLookup
                        )
                );
            }
            state.reactionsPagination = listPagination(action.payload);
        })
        .addCase(loadReactions.error, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsLoadingReason = null;
            state.reactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load reactions"
            );
        })

        // Load Reaction Summaries
        .addCase(loadReactionSummaries.request.type, (state, action) => {
            const typedAction = action as typeof action & LoadReasonMeta;
            state.reactionSummariesLoading = true;
            state.reactionSummariesLoadingReason =
                typedAction.meta?.loadReason || "initial";
            state.reactionSummariesError = null;
            state.reactionSummariesLoadAttempted = true;
        })
        .addCase(loadReactionSummaries.success, (state, action) => {
            state.reactionSummariesLoading = false;
            state.reactionSummariesLoadingReason = null;
            state.reactionSummaries = toArrayPayload(action.payload);
            state.reactionSummariesPagination = listPagination(action.payload);
        })
        .addCase(loadReactionSummaries.error, (state, action) => {
            state.reactionSummariesLoading = false;
            state.reactionSummariesLoadingReason = null;
            state.reactionSummariesError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load reaction summaries"
            );
        })

        // Load Reaction Types
        .addCase(loadReactionTypes.request, (state) => {
            state.reactionTypesLoading = true;
            state.reactionTypesError = null;
        })
        .addCase(loadReactionTypes.success, (state, action) => {
            state.reactionTypesLoading = false;
            state.reactionTypes = action.payload;
        })
        .addCase(loadReactionTypes.error, (state, action) => {
            state.reactionTypesLoading = false;
            state.reactionTypesError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load reaction types"
            );
        })

        // Create Reaction
        .addCase(createReaction.request, (state) => {
            state.reactionsLoading = true;
            state.reactionsError = null;
        })
        .addCase(createReaction.success, (state, action) => {
            state.reactionsLoading = false;
            state.reactions.push(action.payload);
        })
        .addCase(createReaction.error, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to create reaction"
            );
        })

        // Update Reaction
        .addCase(updateReaction.request, (state) => {
            state.reactionsLoading = true;
            state.reactionsError = null;
        })
        .addCase(
            updateReaction.success,
            (state, action: { payload: ILibraryReaction }) => {
                state.reactionsLoading = false;
                const index = state.reactions.findIndex(
                    (r) => r.ui_id === action.payload.ui_id
                );
                if (index !== -1) {
                    state.reactions[index] = action.payload;
                }
            }
        )
        .addCase(updateReaction.error, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to update reaction"
            );
        })

        // Delete Reaction
        .addCase(deleteReaction.request, (state) => {
            state.reactionsLoading = true;
            state.reactionsError = null;
        })
        .addCase(deleteReaction.success.type, (state, action) => {
            const typedAction = action as typeof action & DeletedEntityMeta;
            state.reactionsLoading = false;
            const deletedReactionId =
                typedAction.meta?.deletedReactionId ||
                parseDeleteIdFromEndpoint(
                    typedAction.meta?.rsaa_endpoint,
                    "reactions"
                );
            if (deletedReactionId) {
                state.reactions = state.reactions.filter(
                    (reaction) => reaction.ui_id !== deletedReactionId
                );
            }
        })
        .addCase(deleteReaction.error, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to delete reaction"
            );
        })

        // Add Chemical to Reaction
        .addCase(addChemicalToReaction.request, (state) => {
            state.reactionsLoading = true;
            state.reactionsError = null;
        })
        .addCase(
            addChemicalToReaction.success,
            (state, action: { payload: IReactionChemical }) => {
                state.reactionsLoading = false;
                // Find the reaction and add the chemical to it
                const reaction = state.reactions.find(
                    (r) => r.ui_id === action.payload.reaction_ui_id
                );
                if (reaction) {
                    if (!reaction.chemicals) {
                        reaction.chemicals = [];
                    }
                    reaction.chemicals.push(action.payload);
                }
            }
        )
        .addCase(addChemicalToReaction.error, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to add chemical to reaction"
            );
        })

        // Remove Chemical from Reaction
        .addCase(removeChemicalFromReaction.request, (state) => {
            state.reactionsLoading = true;
            state.reactionsError = null;
        })
        .addCase(removeChemicalFromReaction.success, (state) => {
            state.reactionsLoading = false;
            // We would need the reactionId and chemicalId from the action meta to remove
            // For now, we'll reload the reactions list
        })
        .addCase(removeChemicalFromReaction.error, (state, action) => {
            state.reactionsLoading = false;
            state.reactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to remove chemical from reaction"
            );
        })

        // Load Multi-Step Reactions
        .addCase(loadMultiStepReactions.request.type, (state, action) => {
            const typedAction = action as typeof action & LoadReasonMeta;
            state.multiStepReactionsLoading = true;
            state.multiStepReactionsLoadingReason =
                typedAction.meta?.loadReason || "initial";
            state.multiStepReactionsError = null;
            state.multiStepReactionsLoadAttempted = true;
        })
        .addCase(loadMultiStepReactions.success, (state, action) => {
            state.multiStepReactionsLoading = false;
            state.multiStepReactionsLoadingReason = null;
            const routes = toArrayPayload(action.payload);
            const routeLookup = buildRouteLookup(routes);
            state.multiStepReactions = routes.map((route) =>
                normalizeLibraryMultiStepReaction(route, new Map(), routeLookup)
            );
            state.multiStepReactionsPagination = listPagination(action.payload);
        })
        .addCase(loadMultiStepReactions.error, (state, action) => {
            state.multiStepReactionsLoading = false;
            state.multiStepReactionsLoadingReason = null;
            state.multiStepReactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load multi-step reactions"
            );
        })

        // Load Reaction Route Summaries
        .addCase(loadReactionRouteSummaries.request.type, (state, action) => {
            const typedAction = action as typeof action & LoadReasonMeta;
            state.reactionRouteSummariesLoading = true;
            state.reactionRouteSummariesLoadingReason =
                typedAction.meta?.loadReason || "initial";
            state.reactionRouteSummariesError = null;
            state.reactionRouteSummariesLoadAttempted = true;
        })
        .addCase(loadReactionRouteSummaries.success, (state, action) => {
            state.reactionRouteSummariesLoading = false;
            state.reactionRouteSummariesLoadingReason = null;
            state.reactionRouteSummaries = toArrayPayload(action.payload);
            state.reactionRouteSummariesPagination = listPagination(
                action.payload
            );
        })
        .addCase(loadReactionRouteSummaries.error, (state, action) => {
            state.reactionRouteSummariesLoading = false;
            state.reactionRouteSummariesLoadingReason = null;
            state.reactionRouteSummariesError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load reaction route summaries"
            );
        })

        // Load Reaction Route Stages
        .addCase(loadReactionRouteStages.request.type, (state, action) => {
            const typedAction = action as typeof action & LoadReasonMeta;
            state.reactionRouteStagesLoading = true;
            state.reactionRouteStagesLoadingReason =
                typedAction.meta?.loadReason || "initial";
            state.reactionRouteStagesError = null;
            state.reactionRouteStagesLoadAttempted = true;
        })
        .addCase(loadReactionRouteStages.success, (state, action) => {
            state.reactionRouteStagesLoading = false;
            state.reactionRouteStagesLoadingReason = null;
            state.reactionRouteStages = toArrayPayload(action.payload);
            state.reactionRouteStagesPagination = listPagination(
                action.payload
            );
        })
        .addCase(loadReactionRouteStages.error, (state, action) => {
            state.reactionRouteStagesLoading = false;
            state.reactionRouteStagesLoadingReason = null;
            state.reactionRouteStagesError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to load route stages"
            );
        })

        // Create Multi-Step Reaction
        .addCase(createMultiStepReaction.request, (state) => {
            state.multiStepReactionsLoading = true;
            state.multiStepReactionsError = null;
        })
        .addCase(createMultiStepReaction.success, (state, action) => {
            state.multiStepReactionsLoading = false;
            state.multiStepReactions.push(
                normalizeLibraryMultiStepReaction(action.payload)
            );
        })
        .addCase(createMultiStepReaction.error, (state, action) => {
            state.multiStepReactionsLoading = false;
            state.multiStepReactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to create multi-step reaction"
            );
        })

        // Update Multi-Step Reaction
        .addCase(updateMultiStepReaction.request, (state) => {
            state.multiStepReactionsLoading = true;
            state.multiStepReactionsError = null;
        })
        .addCase(
            updateMultiStepReaction.success,
            (state, action: { payload: ILibraryMultiStepReaction }) => {
                state.multiStepReactionsLoading = false;
                const index = state.multiStepReactions.findIndex(
                    (r) => r.ui_id && r.ui_id === action.payload.ui_id
                );
                if (index !== -1) {
                    state.multiStepReactions[index] =
                        normalizeLibraryMultiStepReaction(action.payload);
                }
            }
        )
        .addCase(updateMultiStepReaction.error, (state, action) => {
            state.multiStepReactionsLoading = false;
            state.multiStepReactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to update multi-step reaction"
            );
        })

        // Delete Multi-Step Reaction
        .addCase(deleteMultiStepReaction.request, (state) => {
            state.multiStepReactionsLoading = true;
            state.multiStepReactionsError = null;
        })
        .addCase(deleteMultiStepReaction.success.type, (state, action) => {
            const typedAction = action as typeof action & DeletedEntityMeta;
            state.multiStepReactionsLoading = false;
            const deletedRouteId =
                typedAction.meta?.deletedRouteId ||
                parseDeleteIdFromEndpoint(
                    typedAction.meta?.rsaa_endpoint,
                    "reaction-routes"
                );
            if (deletedRouteId) {
                state.multiStepReactions = state.multiStepReactions.filter(
                    (route) => route.ui_id !== deletedRouteId
                );
            }
        })
        .addCase(deleteMultiStepReaction.error, (state, action) => {
            state.multiStepReactionsLoading = false;
            state.multiStepReactionsError = resolveRsaaErrorMessage(
                action.payload,
                "Failed to delete route"
            );
        })

        // Reset load attempts
        .addCase(resetLoadAttempts, (state) => {
            state.chemicalsLoadAttempted = false;
            state.librarySummaryLoadAttempted = false;
            state.reactionsLoadAttempted = false;
            state.reactionSummariesLoadAttempted = false;
            state.multiStepReactionsLoadAttempted = false;
            state.reactionRouteSummariesLoadAttempted = false;
            state.reactionRouteStagesLoadAttempted = false;
        });
});

export default library;
